import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { isValidSSHKey } from "src/ui/utils/validityChecker";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning, SshKey } from "@dashboard/provisioning/types";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";

const initialValues = { name: undefined, key: undefined, comment: null };

type FormValues = SshKey;

export const SshKeys = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "SshKeys");
    const parentForm = useForm();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const sshKeys = parentFormValues?.settings?.authorizedSshKeys || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (sshKeys.length) {
            parentForm.change("settings.authorizedSshKeys", [...sshKeys, newValue]);
        }
        if (!sshKeys.length) {
            parentForm.change("settings.authorizedSshKeys", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        if (!values.name) {
            errors.name = t("pages:provisioningPage.input.name.validation.empty");
        }
        if (!values.key) {
            errors.key = t("pages:provisioningPage.input.sshKey.validation.empty");
        }
        // `values` is the live form object, never an element of the list, so the previous
        // `includes` compared references and could never match - the message below was unreachable.
        if (sshKeys.some((sshKey) => sshKey.name === values.name)) {
            errors.name = t("pages:provisioningPage.input.name.validation.alreadyAdded");
        }
        if (!isValidSSHKey(values.key)) {
            errors.key = t("pages:provisioningPage.input.sshKey.validation.invalid");
        }
        return errors;
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.authorizedSshKeys",
            sshKeys.filter((_, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={value} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>{t("pages:provisioningPage.section.sshKeys.title")}</CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>{t("pages:provisioningPage.section.sshKeys.description")}</SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography>{t("pages:provisioningPage.table.header.name")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography>{t("common:label.comment")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {sshKeys.length ? (
                                        sshKeys.map((item, index) => (
                                            <MinimalTableRow key={item.name}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{item.name}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                        name="ssh-key-delete"
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={3}>
                                                <Typography>{t("pages:provisioningPage.table.noSshKeys")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        <Row style={{ paddingBottom: theme.spacing(3), marginTop: theme.spacing(4) }}>
                            <TextField
                                name="name"
                                label={t("pages:provisioningPage.input.name.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField
                                name="key"
                                label={t("pages:provisioningPage.input.sshKey.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row>
                            <TextField
                                name="comment"
                                label={t("common:label.comment")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <PrimaryOutlinedButton onClick={form.submit} disabled={!provisionings.length} name="ssh-key-add">
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
